import Head from 'next/head'
import Link from 'next/link'
import * as React from 'react'
import { NextSeo } from 'next-seo'
import { Grid, Table, Text, Title, Button, Group } from '@mantine/core'
import { IconArrowRight } from '@tabler/icons-react'

import * as Types from '../declarations'

import PageTemplate from './PageTemplate'
import CategoryPageTripRouteMap from './CategoryPageTripRouteMap'
import TrekCard from './TrekCard'
import JustifiedGallery from './TrekPage/components/JustifiedGallery'

import ImageModal from 'components/TrekPage/components/ImageModal'
import HTMLContent from 'components/HTMLContent'

import { tripPageTripToTripCard } from '../lib/helper'
import { formatMonthNumbers, getCountryGuidePageRoute } from '../lib/display'
import { useIsMobile } from 'lib/useIsMobile'
import AccountNudge from 'components/AccountNudge'
import { TripList } from 'components/TripList'

interface Props {
	version: Types.VERSION
	seo: {
		title: string
		description: string
		canonical: string
		siteName: string
		images: {
			url: string
			width?: number
			height?: number
			alt?: string
			type: string
		}[]
	}
	category: string
	continent: string
	uniqueBestMonths: number[]
	trips: Types.DB.Models.TripAttributes[]
	allUploads: Types.DB.Models.UploadAttributes[]
	countryRecord?: Types.DB.Models.CountryAttributes
	countryISOCode: string
	guideCount: number
}

const CountryPage = ({
	seo,
	version,
	category,
	continent,
	uniqueBestMonths,
	trips,
	allUploads,
	countryRecord,
	countryISOCode,
	guideCount,
}: Props) => {
	const isMobile = useIsMobile()

	const [isHydrated, setIsHydrated] = React.useState(false)

	React.useEffect(() => {
		setIsHydrated(true)
	}, [])

	const aggregatedTrips = React.useMemo(() => {
		const mostPopular: Types.DB.Models.TripAttributes[] = []
		const lesserKnown: Types.DB.Models.TripAttributes[] = []
		const mostDifficult: Types.DB.Models.TripAttributes[] = []
		const remaining: Types.DB.Models.TripAttributes[] = []

		if (!countryRecord) {
			return {
				mostPopular,
				lesserKnown,
				mostDifficult,
				remaining: trips,
			}
		}

		const {
			mostPopular: mostPopularHighlights,
			lesserKnown: lesserKnownHighlights,
			mostDifficult: mostDifficultHighlights,
		} = countryRecord?.json?.highlights

		for (let i = 0; i < trips.length; i++) {
			const trip = trips[i]
			switch (true) {
				case mostPopularHighlights.includes(trip.uniqueTripId):
					mostPopular.push(trip)
					break
				case lesserKnownHighlights.includes(trip.uniqueTripId):
					lesserKnown.push(trip)
					break
				case mostDifficultHighlights.includes(trip.uniqueTripId):
					mostDifficult.push(trip)
					break
				default:
					remaining.push(trip)
					break
			}
		}

		return {
			mostPopular,
			lesserKnown,
			mostDifficult,
			remaining,
		}
	}, [trips, countryRecord?.json])

	const climbDifficulties = React.useMemo(() => {
		if (version !== Types.VERSION.PEAKS) {
			return []
		}

		const difficulties = [
			{
				simple: 1,
				code: 'F',

				description:
					'Easy. Suitable for beginners, minimal technical skills required.',
				count: 0,
			},
			{
				simple: 2,
				code: 'PD',
				description:
					'Not very difficult. Some technical skills required, with moderate exposure.',
				count: 0,
			},
			{
				simple: 3,
				code: 'AD',
				description:
					'Fairly difficult. Requires good technical skills and experience, with significant exposure.',
				count: 0,
			},
			{
				simple: 4,
				code: 'D',
				description:
					'Difficult. Requires advanced technical skills and experience, with high exposure.',
				count: 0,
			},
			{
				simple: 5,
				code: 'TD',
				description:
					'Very difficult. Requires very advanced technical skills, experience, and serious commitment.',
				count: 0,
			},
			{
				simple: 6,
				code: 'ED',
				description:
					'Extremely difficult. Requires exceptional technical skills, experience, and high commitment.',
				count: 0,
			},
		]

		for (let i = 0; i < trips.length; i++) {
			const trip = trips[i]
			const simpleDifficulties = trip.jsonData.climbDifficulty?.simple ?? []
			for (let j = 0; j < simpleDifficulties.length; j++) {
				const difficultyCode = simpleDifficulties[j]
				const difficulty = difficulties.find((d) => d.simple === difficultyCode)
				if (difficulty) {
					difficulty.count++
				}
			}
		}

		return difficulties
	}, [version, trips])

	const guideCosts = React.useMemo(() => {
		//  make an array purely of guide costs
		const costs: number[] = []
		for (let i = 0; i < trips.length; i++) {
			const trip = trips[i]
			for (let j = 0; j < (trip?.guideTrips ?? []).length; j++) {
				const guideTrip = trip.guideTrips?.[j]
				if (guideTrip?.price) {
					costs.push(guideTrip?.price)
				}
			}
		}
		// derive a min, max, and average.
		const min = costs.length > 0 ? Math.min(...costs) : 0
		const max = costs.length > 0 ? Math.max(...costs) : 0
		const total = costs.reduce((sum, cost) => +sum + +cost, 0)
		const average = costs.length > 0 ? total / costs.length : 0

		const summary = {
			min,
			max,
			average: parseFloat(average.toFixed(0)),
			count: costs.length,
		}

		return summary
	}, [trips])

	const [lightboxIndex, setLightboxIndex] = React.useState(-1)

	const lightboxThumbClicked = (idClicked: number) => {
		const imgKey = allUploads.findIndex(({ id }) => id === idClicked)
		setLightboxIndex(imgKey)
	}

	const capitalisedCategory =
		category.charAt(0).toUpperCase() + category.slice(1)

	const homePageTrips = React.useMemo(() => {
		return trips.map((trip) => ({
			id: trip.uniqueTripId,
			uniqueTripId: trip.uniqueTripId,
			name: trip.name,
			description: trip.jsonData?.description ?? '',
			slug: trip.slug,
			firstUploadId: trip.uploads?.[0]?.id,
			countries: trip.jsonData.countries,
			continent: trip.jsonData.continent,
			height: trip.jsonData.highLevelInfo?.climb?.maxElevation,
			lowestCost: trip.guideTrips?.[0]?.price ?? null,
			bestMonths: trip.jsonData.highLevelInfo?.whenToGo?.bestMonths ?? [],
			totalDaysLength: trip.jsonData.highLevelInfo?.distances?.total?.days
				? [trip.jsonData.highLevelInfo.distances.total.days]
				: [],
			trekOrClimb: trip.jsonData.trekOrClimb,
			isValidCompletePage: true,
			climbDifficulty: trip.jsonData.climbDifficulty,
		}))
	}, [trips])

	return (
		<>
			<Head>
				<title>{seo.title}</title>

				<meta name="description" content={seo.description} />
			</Head>
			<NextSeo
				title={seo.title}
				description={seo.description}
				canonical={seo.canonical}
				openGraph={{
					url: seo.canonical,
					title: seo.title,
					description: seo.description,
					images: seo.images,
					siteName: seo.siteName,
					type: 'website',
					locale: 'en_US',
				}}
			/>
			<PageTemplate>
				<div className="country-template">
					<AccountNudge variant="alert" version={version} />
					{/* <Title order={1} mb="lg" style={{ textTransform: 'capitalize' }}>
						{version === Types.VERSION.PEAKS ? 'Mountaineering' : 'Trekking'} in{' '}
						{category}
					</Title>
					<p>
						Discover the best{' '}
						{version === Types.VERSION.PEAKS
							? 'mountain climbing and mountaineering'
							: 'trekking'}{' '}
						routes in {category}.
					</p> */}
					{countryRecord?.json.blurb && (
						<>
							<Title order={2} mb="md">
								Why {version === Types.VERSION.TREKKING ? 'trek' : 'climb'} in{' '}
								{capitalisedCategory}
							</Title>
							<HTMLContent html={countryRecord?.json.blurb}></HTMLContent>
						</>
					)}
					<Title order={2} mb="md">
						When to {version === Types.VERSION.TREKKING ? 'trek' : 'climb'} in{' '}
						{capitalisedCategory}
					</Title>
					<div>
						The best time to go{' '}
						{version === Types.VERSION.TREKKING ? 'trekking' : 'climbing'} in{' '}
						{capitalisedCategory} is:
						<ul>
							{formatMonthNumbers(uniqueBestMonths).map(
								(monthRange, monthRangeIndex) => (
									<li key={monthRangeIndex}>{monthRange}</li>
								),
							)}
						</ul>
					</div>
					{countryRecord?.json.whenToClimb && (
						<HTMLContent html={countryRecord?.json.whenToClimb}></HTMLContent>
					)}
					<Title order={2} mb="md">
						Where to {version === Types.VERSION.TREKKING ? 'trek' : 'climb'} in{' '}
						{capitalisedCategory}
					</Title>
					<Grid mt="md">
						<Grid.Col
							span={
								(isHydrated && isMobile) ||
								!countryRecord?.json.mainRegionsSummary
									? 12
									: 6
							}
						>
							<CategoryPageTripRouteMap trips={trips} alt={seo.title} />
						</Grid.Col>
						<Grid.Col
							span={
								!countryRecord?.json.mainRegionsSummary
									? 0
									: isHydrated && isMobile
									? 12
									: 6
							}
						>
							{countryRecord?.json.mainRegionsSummary && (
								<>
									<HTMLContent
										html={countryRecord?.json.mainRegionsSummary}
									></HTMLContent>
								</>
							)}
						</Grid.Col>
					</Grid>

					<Title order={2} mt="md">
						{version === Types.VERSION.TREKKING ? 'Treks' : 'Climbs'} in{' '}
						{capitalisedCategory}
					</Title>
					<Text mt="lg">
						There are {trips.length}{' '}
						{version === Types.VERSION.TREKKING ? 'Treks' : 'Climbs'} in{' '}
						{capitalisedCategory}. Get an idea of what separates them here:
					</Text>
					{aggregatedTrips.mostPopular.length > 0 && (
						<>
							<Title order={3}>Most popular</Title>
							<p>
								The classic{' '}
								{version === Types.VERSION.TREKKING ? 'treks' : 'climbs'} in{' '}
								{capitalisedCategory}:
							</p>
							<div id="trip-items" className="trek-card-container">
								{aggregatedTrips.mostPopular?.map((trip, key) => (
									<TrekCard
										tripCardProps={tripPageTripToTripCard(trip)}
										key={key}
									/>
								))}
							</div>
						</>
					)}
					{aggregatedTrips.lesserKnown.length > 0 && (
						<>
							<Title order={3} mt="lg">
								Lesser known
							</Title>
							<p>
								Escape the crowds and get of the beaten path in{' '}
								{capitalisedCategory} with these{' '}
								{version === Types.VERSION.TREKKING ? 'treks' : 'climbs'}:
							</p>
							<div id="trip-items" className="trek-card-container">
								{aggregatedTrips.lesserKnown?.map((trip, key) => (
									<TrekCard
										tripCardProps={tripPageTripToTripCard(trip)}
										key={key}
									/>
								))}
							</div>
						</>
					)}
					{aggregatedTrips.mostDifficult.length > 0 && (
						<>
							<Title order={3} mt="lg">
								Most difficult
							</Title>
							<p>
								If you want to challenge yourself, consider these{' '}
								{version === Types.VERSION.TREKKING ? 'treks' : 'climbs'} in{' '}
								{capitalisedCategory}:
							</p>
							<div id="trip-items" className="trek-card-container">
								{aggregatedTrips.mostDifficult?.map((trip, key) => (
									<TrekCard
										tripCardProps={tripPageTripToTripCard(trip)}
										key={key}
									/>
								))}
							</div>
						</>
					)}

					{version === Types.VERSION.PEAKS && (
						<>
							<Title order={3} mt="xl" mb="md">
								All {version === Types.VERSION.PEAKS ? 'climbs' : 'treks'} in{' '}
								{capitalisedCategory}
							</Title>
							<TripList
								treks={
									(homePageTrips as unknown) as Types.App.ExpeditionListTrip[]
								}
								version={version}
							/>
						</>
					)}

					{version === Types.VERSION.PEAKS && (
						<>
							<Title order={3} mt="lg" mb="md">
								Climb difficulties in {capitalisedCategory}
							</Title>
							<p>How many of each difficulty grade can you find a route for.</p>
							<Table
								horizontalSpacing="md"
								verticalSpacing="md"
								fz="md"
								highlightOnHover
								mt="lg"
							>
								<thead>
									<tr>
										<th>French Alpine</th>
										<th>Difficulty description</th>
										<th>Quantity</th>
									</tr>
								</thead>
								<tbody>
									{climbDifficulties.map((diff, key) => (
										<tr key={key}>
											<td>{diff.code}</td>
											<td>{diff.description}</td>
											<td>{diff.count}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</>
					)}

					{guideCosts.count > 0 && (
						<>
							<Title order={2} mt="lg" mb="md" id="guides">
								Guides with trips in {capitalisedCategory}
							</Title>
							{/* <Title order={3} mt="lg" mb="md">
								Guide costs for{' '}
								{version === Types.VERSION.TREKKING ? 'trekking' : 'climbing'}{' '}
								in {capitalisedCategory}
							</Title> */}
							<p>
								There are {guideCosts.count} guided trips listed for{' '}
								{capitalisedCategory}. Trips range from $
								{guideCosts.min.toLocaleString()} to $
								{guideCosts.max.toLocaleString()}, and average $
								{guideCosts.average.toLocaleString()} per{' '}
								{version === Types.VERSION.TREKKING ? 'trek' : 'expedition'}.
							</p>

							<Title order={4} mb="lg">
								Explore our directory of {guideCount} guides who operate trips
								in {capitalisedCategory}:{' '}
								<Button
									size={'xs'}
									component={Link}
									rightSection={<IconArrowRight size={16} />}
									href={getCountryGuidePageRoute(
										(version === Types.VERSION.PEAKS
											? 'CLIMB'
											: 'TREK') as Types.App.DataTypes.TrekOrClimb,
										continent,
										countryISOCode,
									)}
								>
									{capitalisedCategory} Guides
								</Button>
							</Title>

							<Text mb="lg" mt="md">
								Or check out all guides in the{' '}
								<Link href="/guides">guide directory</Link> to browse all guides
								in one place.
							</Text>
						</>
					)}

					<Title order={2} mt="xl">
						Pictures from{' '}
						{version === Types.VERSION.TREKKING ? 'trekking' : 'climbing'} in{' '}
						{capitalisedCategory}
					</Title>
					<ImageModal
						activeImageIndex={lightboxIndex}
						images={allUploads}
						changeActiveImageIndex={setLightboxIndex}
					/>
					<Text mt="lg">
						These are pictures taken while climbing in {capitalisedCategory}.
						They&apos;re shown here to give you an idea of what the route is
						like.
					</Text>

					<JustifiedGallery
						pictures={allUploads}
						size="small"
						altText={`${
							version === Types.VERSION.TREKKING ? 'trekking' : 'mountaineering'
						} in ${capitalisedCategory}`}
						onImageClick={lightboxThumbClicked}
					/>
				</div>
			</PageTemplate>
		</>
	)
}

export default CountryPage
